// components/Header.tsx
import { useCallback, useState } from 'react';
import { Container, Navbar, Nav, Offcanvas, NavDropdown, Row, Col, Badge } from 'react-bootstrap';
import { ShoppingCartIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Logo, LogoSmall } from '../images';
import { SearchPopup } from './search-popup';
import { CartItem, useCart } from '../context/cart-context';
import { Link, useNavigate } from 'react-router-dom';
import {} from '../'
import { LocalCurrency } from '../types';
import { thousandSeparator } from '../helper/utils';
import QuantityConfig from './quantity-config';
import { useCartActions } from '../hooks/use-cart-actions';
import { useAuth } from '../context/auth-context';
interface NavItem {
  title: string;
  children?: NavItem[];
  href: string;
}

const Header = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const { cartCount, cartItems, cartTotal } = useCart();
  const { handleQuantityChange, handleRemoveFromCart } = useCartActions();
  const { user, isAuth } = useAuth();

  const navigate = useNavigate();

  let navItems: NavItem[] = [
    {
      title: 'Home',
      href: '/'
    },
    {
      title: 'Shop',
      href: '/shop'
    },
    {
      title: 'Services',
      href: '/services'
    },
    // {
    //   title: 'Blog',
    //   href: ''
    // },
    // {
    //   title: 'Contact',
    //   href: ''
    // }
  ];

  if (!isAuth) {
    navItems = [...navItems, {
      title: 'Login',
      href: '/login'
    }];
  }

  const handleVisibility = useCallback(() => setShowSearch(false), [])

  const handleAddedQuantity = useCallback((cartItem: CartItem, qty: number) => handleQuantityChange(cartItem.id, qty) , [handleQuantityChange]);

  const handleHideCartSideMenu = useCallback(() => setShowCart(false), []);

  return (
    <>
      <Navbar expand="lg" bg='white' className="position-sticky top-0 text-uppercase fs-6 p-3 border-bottom" style={{ zIndex: 99 }}>
        <Container fluid>
          <Row className='justify-content-between align-items-center w-100'>
            <Col xs='auto'>
              <Navbar.Brand href="/" bsPrefix='easysauces-logo' className='text-white'>
                <img src={Logo} alt='Easysauce logo' style={{
                  width: '164px',
                }} className='d-none d-md-block' />
                <img src={LogoSmall} alt='Easysauce logo' style={{
                  width: '40px',
                }} className='d-block d-md-none' />
              </Navbar.Brand>
            </Col>

            <Col xs='auto' lg={8}>
              <Navbar.Toggle aria-controls="offcanvasNavbar" onClick={() => setShowNav(true)} />
              <Navbar.Offcanvas
                id="offcanvasNavbar"
                show={showNav}
                onHide={() => setShowNav(false)}
                placement="end"
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>Menu</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <Nav className="justify-content-end flex-grow-1 gap-1 gap-md-5 pe-3">
                    {navItems.map((item, index) => (
                      <Nav.Item key={index}>
                        {item.children ? (
                          <NavDropdown title={item.title} id={`dropdown-${item.title}`}>
                            {item.children.map((child, childIndex) => (
                              <NavDropdown.Item key={childIndex} href={child.href}>
                                {child.title}
                              </NavDropdown.Item>
                            ))}
                          </NavDropdown>
                        ) : (
                          <Link to={item.href}>{item.title}</Link>
                        )}
                      </Nav.Item>
                    ))}
                  </Nav>
                </Offcanvas.Body>
              </Navbar.Offcanvas>
            </Col>

            <Col xs={3} lg='auto'>
              <Navbar as='ul' className='list-unstyled d-flex m-0' bsPrefix='bb'>
                
                {
                  user && user.firstName && (
                    <Nav.Item as='li' className='d-none d-lg-block' bsPrefix='cart-txt'>
                      <Nav.Link as={Link} to='/customer/dashboard' className='text-uppercase mx-3' aria-controls="offcanvasCart" bsPrefix='cart-box'>
                        <Container as='span' className='cart-count' style={{margin: 0, padding: '4px'}}>
                          { user.lastName }
                        </Container>
                      </Nav.Link>
                    </Nav.Item>
                  )
                }
                <Nav.Item as='li' className='d-block' bsPrefix='cart-box'>
                  <Nav.Link className='mx-2 position-relative' onClick={() => setShowCart(true)} bsPrefix='none' aria-controls="offcanvasCart">
                    <ShoppingCartIcon width={20} height={20} />
                    <Badge 
                      as='span' 
                      bg='warning' 
                      pill={true} 
                      text='dark'
                      style={{
                        position: 'absolute',
                        top: '-12px',
                        right: '-8px'
                      }}
                    >{cartCount}</Badge>
                  </Nav.Link>
                </Nav.Item>
                {/* <Nav.Item as='li' className='search-box mx-2' bsPrefix='search-box'>
                  <Nav.Link className='search-button' bsPrefix='none' onClick={() => setShowSearch(!showSearch)} aria-controls="offcanvasCart">
                    <MagnifyingGlassIcon width={25} height={25} />
                  </Nav.Link>
                </Nav.Item> */}
              </Navbar>
            </Col>
          </Row>
        </Container>
      </Navbar>

      {/* Search Popup */}
      <SearchPopup visible={ showSearch } handleVisibility={handleVisibility} />

      {/* Cart Offcanvas */}
      <Offcanvas show={showCart} onHide={handleHideCartSideMenu} placement="end">
        <Offcanvas.Header closeButton  className='d-flex justify-content-between align-items-center'>
          <Offcanvas.Title className='d-flex justify-content-between align-items-center flex-grow-1 me-3'>
            <span className="text-primary">Your cart</span>
            <span className="badge bg-primary rounded-pill">{ cartCount }</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Cart items */}
          <ul className="list-group mb-3">
            {cartItems.map(cartItem => (
              <li className="list-group-item pb-3 mb-2 d-flex justify-content-between lh-sm border-bottom" key={cartItem.id}>
                <Container fluid>
                  <Row>
                    <Col xs={7} className='mx-0 px-0'>
                      <h6 className="my-0">{ cartItem.name }</h6>
                      <span 
                        className="text-body-secondary"
                        style={{
                          fontSize: '.8rem'
                        }}
                      >{cartItem.currency}{ thousandSeparator(cartItem.price.toFixed(2)) } x {cartItem.quantity}</span> 
                    </Col>
                    <Col xs={5} className='px-0 d-flex justify-content-end align-items-start flex-row'>
                      <QuantityConfig size='sm' currentQty={cartItem.quantity} handleQuantitySetter={(qty: number) => handleAddedQuantity(cartItem, qty)} />
                    </Col>
                    <Col 
                      xs={12} 
                      md={12} 
                      style={{ fontSize: '.7rem', lineHeight: '0', cursor: 'pointer' }} 
                      className='text-danger p-0 mt-1'
                      onClick={() => handleRemoveFromCart(cartItem.id)}
                    >
                      <TrashIcon className='text-danger' width={15} height={15} />Remove
                    </Col>
                  </Row>
                </Container>
              </li>
            ))}
            {cartTotal > 0 && (
              <li className="list-group-item d-flex justify-content-between">
                <span>Total ({LocalCurrency.naira})</span>
                <strong>{LocalCurrency.naira}{thousandSeparator(cartTotal.toFixed(2))}</strong>
              </li>
            )}
          </ul>
          {cartTotal > 0 && (<Row>
            <Col xs={6}><button onClick={handleHideCartSideMenu } className="w-100 btn btn-primary btn-sm rounded" type="submit">Continue shopping</button></Col>
            <Col xs={6}><button onClick={() => {navigate('/checkout'); handleHideCartSideMenu(); }} className="w-100 btn btn-success btn-sm rounded" type="submit">Continue to checkout</button></Col>
            </Row>)
          }
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default Header;